import {
  canonicalOrderChannelForSource,
  type ExternalChannelSource,
  type OrderChannel,
} from '../contracts/operating-context.ts';

export type ChannelSource = ExternalChannelSource;
export type { OrderChannel } from '../contracts/operating-context.ts';

export type ChannelPaymentMode = 'pay-at-store' | 'electronic-proof';

export interface ChannelOptionSelectionInput {
  readonly optionGroupId: string;
  readonly optionIds: readonly string[];
}

export interface ChannelLineInput {
  readonly lineId: string;
  readonly productId: string;
  readonly quantity: number;
  readonly selections: readonly ChannelOptionSelectionInput[];
  readonly note?: string;
}

export interface ChannelPayload {
  readonly lines: readonly ChannelLineInput[];
  readonly customer?: {
    readonly name?: string;
    readonly phone?: string;
    readonly note?: string;
  };
  readonly pickup?: {
    readonly mode?: string;
    readonly requestedTime?: string;
  };
  readonly customerDisplayedTotalMinor?: number;
  readonly currency?: string;
  /** Customer/channel payment intent only. It never means settled/approved. */
  readonly paymentMode?: ChannelPaymentMode;
  /** Durable evidence reference supplied by an upload/storage boundary. It is not settlement truth. */
  readonly paymentEvidenceRef?: string;
  readonly externalReference?: string;
  readonly note?: string;
}

export interface ChannelSubmission {
  readonly submissionId: string;
  readonly idempotencyKey: string;
  readonly source: ChannelSource;
  readonly channel: OrderChannel;
  readonly createdAt: string;
  readonly businessDate: string;
  readonly payload: ChannelPayload;
}

export interface ChannelReconciliation {
  readonly runtimeIntakeId: string;
  readonly orderId?: string;
  readonly decision: 'accepted' | 'rejected';
  readonly reason?: string;
  readonly reconciledAt: string;
}

export interface ChannelEnvelope extends ChannelSubmission {
  readonly status: 'submitted' | 'accepted' | 'rejected';
  readonly authoritativeAcceptance: boolean;
  readonly runtimeIntakeId?: string;
  readonly orderId?: string;
  readonly reason?: string;
  readonly reconciledAt?: string;
}

interface StoredSubmission {
  readonly signature: string;
  envelope: ChannelEnvelope;
  reconciliationSignature?: string;
}

const MAX_LINES = 80;
const MAX_QUANTITY = 20;
const MAX_SELECTION_GROUPS = 30;
const MAX_OPTION_IDS_PER_GROUP = 20;

const ALLOWED_PAYLOAD_KEYS = new Set([
  'lines', 'customer', 'pickup', 'customerDisplayedTotalMinor', 'currency',
  'paymentMode', 'paymentEvidenceRef', 'externalReference', 'note',
]);
const ALLOWED_LINE_KEYS = new Set(['lineId', 'productId', 'quantity', 'selections', 'note']);
const ALLOWED_SELECTION_KEYS = new Set(['optionGroupId', 'optionIds']);
const ALLOWED_CUSTOMER_KEYS = new Set(['name', 'phone', 'note']);
const ALLOWED_PICKUP_KEYS = new Set(['mode', 'requestedTime']);

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));

const assertText = (value: string, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const assertTimestamp = (value: string, code: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error(code);
};

const assertAllowedKeys = (value: object, allowed: ReadonlySet<string>, code: string): void => {
  for (const key of Object.keys(value)) if (!allowed.has(key)) throw new Error(`${code}:${key}`);
};

const trimOptional = (value: string | undefined, max: number, code: string): string | undefined => {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  if (trimmed.length > max) throw new Error(code);
  return trimmed || undefined;
};

const normalizePaymentMode = (value: ChannelPaymentMode | undefined): ChannelPaymentMode | undefined => {
  if (value === undefined) return undefined;
  if (value !== 'pay-at-store' && value !== 'electronic-proof') throw new Error('CHANNEL_PAYMENT_MODE_INVALID');
  return value;
};

const normalizePayload = (payload: ChannelPayload): ChannelPayload => {
  assertAllowedKeys(payload as unknown as object, ALLOWED_PAYLOAD_KEYS, 'CHANNEL_BUSINESS_FIELDS_FORBIDDEN');
  if (!Array.isArray(payload.lines) || payload.lines.length === 0) throw new Error('CHANNEL_LINES_REQUIRED');
  if (payload.lines.length > MAX_LINES) throw new Error('CHANNEL_LINES_LIMIT_EXCEEDED');

  const lineIds = new Set<string>();
  const lines = payload.lines.map((line): ChannelLineInput => {
    assertAllowedKeys(line as unknown as object, ALLOWED_LINE_KEYS, 'CHANNEL_LINE_FIELDS_FORBIDDEN');
    const lineId = line.lineId.trim();
    const productId = line.productId.trim();
    assertText(lineId, 'CHANNEL_LINE_ID_REQUIRED');
    assertText(productId, 'CHANNEL_PRODUCT_ID_REQUIRED');
    if (lineIds.has(lineId)) throw new Error('CHANNEL_LINE_ID_DUPLICATE');
    lineIds.add(lineId);
    if (!Number.isSafeInteger(line.quantity) || line.quantity <= 0) throw new Error('CHANNEL_QUANTITY_INVALID');
    if (line.quantity > MAX_QUANTITY) throw new Error('CHANNEL_QUANTITY_LIMIT_EXCEEDED');
    if (!Array.isArray(line.selections)) throw new Error('CHANNEL_SELECTIONS_INVALID');
    if (line.selections.length > MAX_SELECTION_GROUPS) throw new Error('CHANNEL_SELECTIONS_LIMIT_EXCEEDED');

    const groups = new Set<string>();
    const selections = line.selections.map((selection: ChannelOptionSelectionInput): ChannelOptionSelectionInput => {
      assertAllowedKeys(selection as unknown as object, ALLOWED_SELECTION_KEYS, 'CHANNEL_SELECTION_FIELDS_FORBIDDEN');
      const optionGroupId = selection.optionGroupId.trim();
      assertText(optionGroupId, 'CHANNEL_OPTION_GROUP_ID_REQUIRED');
      if (groups.has(optionGroupId)) throw new Error('CHANNEL_SELECTION_GROUP_DUPLICATE');
      groups.add(optionGroupId);
      if (!Array.isArray(selection.optionIds)) throw new Error('CHANNEL_OPTION_IDS_INVALID');
      if (selection.optionIds.length > MAX_OPTION_IDS_PER_GROUP) throw new Error('CHANNEL_OPTION_IDS_LIMIT_EXCEEDED');
      const optionIds = selection.optionIds.map((optionId: string) => {
        const normalized = optionId.trim();
        assertText(normalized, 'CHANNEL_OPTION_ID_REQUIRED');
        return normalized;
      });
      if (new Set(optionIds).size !== optionIds.length) throw new Error('CHANNEL_OPTION_ID_DUPLICATE');
      return { optionGroupId, optionIds };
    });

    return {
      lineId,
      productId,
      quantity: line.quantity,
      selections,
      note: trimOptional(line.note, 300, 'CHANNEL_LINE_NOTE_LIMIT_EXCEEDED'),
    };
  });

  if (payload.customerDisplayedTotalMinor !== undefined &&
      (!Number.isSafeInteger(payload.customerDisplayedTotalMinor) || payload.customerDisplayedTotalMinor < 0)) {
    throw new Error('CHANNEL_DISPLAYED_TOTAL_INVALID');
  }

  let customer: ChannelPayload['customer'];
  if (payload.customer) {
    assertAllowedKeys(payload.customer as unknown as object, ALLOWED_CUSTOMER_KEYS, 'CHANNEL_CUSTOMER_FIELDS_FORBIDDEN');
    customer = {
      name: trimOptional(payload.customer.name, 80, 'CHANNEL_CUSTOMER_NAME_LIMIT_EXCEEDED'),
      phone: trimOptional(payload.customer.phone, 40, 'CHANNEL_CUSTOMER_PHONE_LIMIT_EXCEEDED'),
      note: trimOptional(payload.customer.note, 500, 'CHANNEL_CUSTOMER_NOTE_LIMIT_EXCEEDED'),
    };
  }

  let pickup: ChannelPayload['pickup'];
  if (payload.pickup) {
    assertAllowedKeys(payload.pickup as unknown as object, ALLOWED_PICKUP_KEYS, 'CHANNEL_PICKUP_FIELDS_FORBIDDEN');
    pickup = {
      mode: trimOptional(payload.pickup.mode, 40, 'CHANNEL_PICKUP_MODE_LIMIT_EXCEEDED'),
      requestedTime: trimOptional(payload.pickup.requestedTime, 80, 'CHANNEL_PICKUP_TIME_LIMIT_EXCEEDED'),
    };
  }

  const currency = trimOptional(payload.currency, 10, 'CHANNEL_CURRENCY_LIMIT_EXCEEDED')?.toUpperCase();
  const paymentMode = normalizePaymentMode(payload.paymentMode);
  const paymentEvidenceRef = trimOptional(payload.paymentEvidenceRef, 200, 'CHANNEL_PAYMENT_EVIDENCE_REF_LIMIT_EXCEEDED');
  if (paymentMode === 'electronic-proof' && !paymentEvidenceRef) throw new Error('CHANNEL_PAYMENT_EVIDENCE_REQUIRED');
  if (paymentMode === 'pay-at-store' && paymentEvidenceRef) throw new Error('CHANNEL_PAYMENT_EVIDENCE_NOT_ALLOWED');
  const externalReference = trimOptional(payload.externalReference, 200, 'CHANNEL_EXTERNAL_REFERENCE_LIMIT_EXCEEDED');
  const note = trimOptional(payload.note, 500, 'CHANNEL_NOTE_LIMIT_EXCEEDED');
  return {
    lines,
    ...(customer ? { customer } : {}),
    ...(pickup ? { pickup } : {}),
    ...(payload.customerDisplayedTotalMinor === undefined ? {} : { customerDisplayedTotalMinor: payload.customerDisplayedTotalMinor }),
    ...(currency ? { currency } : {}),
    ...(paymentMode ? { paymentMode } : {}),
    ...(paymentEvidenceRef ? { paymentEvidenceRef } : {}),
    ...(externalReference ? { externalReference } : {}),
    ...(note ? { note } : {}),
  };
};

const normalizeSubmission = (input: ChannelSubmission): ChannelSubmission => {
  const submissionId = input.submissionId.trim();
  const idempotencyKey = input.idempotencyKey.trim();
  const businessDate = input.businessDate.trim();
  assertText(submissionId, 'CHANNEL_SUBMISSION_ID_REQUIRED');
  assertText(idempotencyKey, 'CHANNEL_IDEMPOTENCY_REQUIRED');
  assertText(businessDate, 'CHANNEL_BUSINESS_DATE_REQUIRED');
  assertTimestamp(input.createdAt, 'CHANNEL_CREATED_AT_INVALID');
  if (canonicalOrderChannelForSource(input.source) !== input.channel) throw new Error('CHANNEL_SOURCE_CHANNEL_MISMATCH');
  return {
    submissionId,
    idempotencyKey,
    source: input.source,
    channel: input.channel,
    createdAt: input.createdAt,
    businessDate,
    payload: normalizePayload(input.payload),
  };
};

export function createChannelIntake() {
  const bySubmission = new Map<string, StoredSubmission>();
  const submissionByIdempotency = new Map<string, string>();

  return {
    submit(input: ChannelSubmission): ChannelEnvelope {
      const normalized = normalizeSubmission(input);
      const signature = signatureOf(normalized);

      const idempotentSubmissionId = submissionByIdempotency.get(normalized.idempotencyKey);
      if (idempotentSubmissionId) {
        const stored = bySubmission.get(idempotentSubmissionId);
        if (!stored) throw new Error('CHANNEL_INTERNAL_IDEMPOTENCY_CORRUPT');
        if (stored.signature !== signature) throw new Error('CHANNEL_IDEMPOTENCY_CONFLICT');
        return clone(stored.envelope);
      }

      const existing = bySubmission.get(normalized.submissionId);
      if (existing) {
        if (existing.signature !== signature) throw new Error('CHANNEL_SUBMISSION_ID_CONFLICT');
        return clone(existing.envelope);
      }

      const envelope: ChannelEnvelope = {
        ...normalized,
        status: 'submitted',
        authoritativeAcceptance: false,
      };
      bySubmission.set(normalized.submissionId, { signature, envelope });
      submissionByIdempotency.set(normalized.idempotencyKey, normalized.submissionId);
      return clone(envelope);
    },

    reconcile(submissionId: string, decision: ChannelReconciliation): ChannelEnvelope {
      const stored = bySubmission.get(submissionId);
      if (!stored) throw new Error('CHANNEL_SUBMISSION_NOT_FOUND');
      assertText(decision.runtimeIntakeId, 'CHANNEL_RUNTIME_INTAKE_ID_REQUIRED');
      assertTimestamp(decision.reconciledAt, 'CHANNEL_RECONCILED_AT_INVALID');
      if (decision.decision === 'accepted') assertText(decision.orderId ?? '', 'CHANNEL_ACCEPTED_ORDER_ID_REQUIRED');
      if (decision.decision === 'rejected') assertText(decision.reason ?? '', 'CHANNEL_REJECTION_REASON_REQUIRED');

      const signature = signatureOf(decision);
      if (stored.reconciliationSignature) {
        if (stored.reconciliationSignature !== signature) throw new Error('CHANNEL_RECONCILIATION_CONFLICT');
        return clone(stored.envelope);
      }

      stored.envelope = {
        ...stored.envelope,
        status: decision.decision,
        authoritativeAcceptance: decision.decision === 'accepted',
        runtimeIntakeId: decision.runtimeIntakeId,
        orderId: decision.orderId,
        reason: decision.reason,
        reconciledAt: decision.reconciledAt,
      };
      stored.reconciliationSignature = signature;
      return clone(stored.envelope);
    },

    get(submissionId: string): ChannelEnvelope | null {
      const stored = bySubmission.get(submissionId);
      return stored ? clone(stored.envelope) : null;
    },

    list(): readonly ChannelEnvelope[] {
      return [...bySubmission.values()].map((stored) => clone(stored.envelope));
    },
  };
}
